import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';
import { SectionHeading } from './Pieces';

/**
 * FAQ accordion with built-in JSON-LD FAQPage schema for SEO.
 * Layout-friendly: drops into any page section without changing visual system.
 *
 * Props:
 *   overline, title, subtitle, items: [{ q, a }], variant: 'light' | 'dark', testIdPrefix
 */
export default function FAQ({
  overline = 'Frequently Asked',
  title = 'Answers For HR, Operations & Partnership Leaders.',
  subtitle,
  items = [],
  variant = 'light',
  testIdPrefix = 'faq',
}) {
  const [open, setOpen] = useState(null);
  const isDark = variant === 'dark';

  useEffect(() => {
    if (!items || items.length === 0) return undefined;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-brp-faq', testIdPrefix);
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const existing = document.head.querySelector(`script[data-brp-faq="${testIdPrefix}"]`);
      if (existing) existing.remove();
    };
  }, [items, testIdPrefix]);

  return (
    <div data-testid={`${testIdPrefix}-section`}>
      <SectionHeading overline={overline} title={title} subtitle={subtitle} light={isDark} />
      <div className="mt-12 max-w-4xl">
        {items.map((it, i) => {
          const expanded = open === i;
          return (
            <div
              key={i}
              className={`${
                isDark ? 'border-white/15' : 'border-[#E5E5E5]'
              } border-t ${i === items.length - 1 ? 'border-b' : ''}`}
            >
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : i)}
                aria-expanded={expanded}
                className={`w-full flex items-start justify-between gap-6 text-left py-6 md:py-7 transition-colors ${
                  isDark ? 'text-white hover:text-[#D4AF37]' : 'text-[#0A0A0A] hover:text-[#D4AF37]'
                }`}
                data-testid={`${testIdPrefix}-item-${i}`}
              >
                <span className="font-display uppercase tracking-wide text-lg md:text-xl font-bold">
                  {it.q}
                </span>
                <span
                  className={`mt-1 flex-shrink-0 w-9 h-9 flex items-center justify-center border ${
                    expanded
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                      : isDark
                      ? 'border-white/40 text-white'
                      : 'border-[#0A0A0A] text-[#0A0A0A]'
                  } transition-colors`}
                >
                  {expanded ? <Minus size={16} /> : <Plus size={16} />}
                </span>
              </button>
              {expanded && (
                <div
                  className={`pb-7 pr-12 leading-relaxed ${
                    isDark ? 'text-gray-300' : 'text-[#555]'
                  }`}
                  data-testid={`${testIdPrefix}-answer-${i}`}
                >
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
